<template>
    <div
        v-if="options"
        v-click-outside="{ handler: () => open = false }"
        class="ds-select"
        :class="{
            'is-active': modelValue !== null,
            'is-underlined': open,
            'is-open': open,
            'is-searchable': searchable,
            'has-value': inputValue,
            'has-error': error,
            'has-icon-before': iconBefore || selectedIconBefore?.code || searchbox,
            basic: !minimal && !searchbox,
            minimal,
            searchbox,
        }"
    >
        <div class="input">
            <DSIcon
                v-if="!!(searchbox || iconBefore || selectedIconBefore?.code)"
                :code="iconBefore?.code || iconBefore || selectedIconBefore?.code || 'search'"
                :size="iconBefore?.size || selectedIconBefore?.size || 'l'"
                class="icon-before"
            />
            <label :for="`combobox-input-${computedId}`" class="label" :class="{ 'sr-only': hideLabel }">{{ label }}</label>
            <input
                :id="`combobox-input-${computedId}`"
                ref="inputRef"
                v-model="inputValue"
                role="combobox"
                class="native-input"
                :type="searchable ? 'text' : 'button'"
                autofill="false"
                autocomplete="off"
                data-lpignore="true"
                :aria-controls="`list-${computedId}`"
                :aria-expanded="open"
                :aria-autocomplete="minimal ? null : 'list'"
                :aria-activedescendant="open ? `option-${computedId}-${currentIndex}` : null"
                :disabled="disabled"
                @click.prevent="onClick"
                @keydown.enter.prevent="onEnter"
                @keydown.esc="open = false"
                @keydown.capture="onKeydown"
                @keydown.up.prevent="keyUpOneItem"
                @keydown.down.prevent="keyDownOneItem"
                @keydown.home.stop="keyDownHome"
                @keydown.end.stop="keyDownEnd"
                @input="onInput"
            />
            <Transition name="fade">
                <button
                    v-show="!searchbox || inputValue"
                    ref="list-toggle"
                    class="list-toggle-btn"
                    :class="searchbox ? 'clear-btn' : 'toggle-btn'"
                    type="button"
                    :aria-controls="`list-${computedId}`"
                    :aria-expanded="open"
                    tabindex="-1"
                    @click="onToggleClick"
                >
                    <DSIcon :code="toggleIcon" size="xs" />
                </button>
            </Transition>
        </div>
        <Transition name="fade-height">
            <div
                v-show="searchbox ? (open && filteredOptions.length && inputValue) : open"
                ref="scrollableListRef"
                class="scrollable-list"
                tabindex="0"
                @click.prevent="onClick(); open = false"
                @keydown.enter.prevent="onEnter"
                @keydown.esc="open = false"
                @keydown.capture="onKeydown"
                @keydown.up.prevent="keyUpOneItem"
                @keydown.down.prevent="keyDownOneItem"
                @keydown.home.prevent="keyDownHome"
                @keydown.end.prevent="keyDownEnd"
            >
                <ul
                    :id="`list-${computedId}`"
                    class="list"
                    :aria-label="label"
                    role="listbox"
                >
                    <li
                        v-for="item, index in filteredOptions"
                        :id="`option-${computedId}-${index}`"
                        :ref="el => listItemRefs[index] = el"
                        :key="`item-${item.label}`"
                        class="list-item"
                        :class="{ 'is-active': index === currentIndex, 'default': !$slots.listItem }"
                        role="option"
                        @click="setActive(item)"
                    >
                        <div
                            v-if="containsListIcons && item.iconBefore && (eagerListIcons || visibleIcons.includes(listItemRefs[index]))"
                            class="item-before"
                        >
                            <DSIcon :code="item.iconBefore?.code || item.iconBefore" :size="item.iconBefore?.size || 'l'" />
                        </div>
                        <slot name="listItem" :item="item">{{ item.label }}</slot>
                    </li>
                </ul>
            </div>
        </Transition>
        <div v-if="error" class="text-after" :class="{ error, 'is-active': error }">{{ error }}</div>
    </div>
</template>

<script setup>
import { ref, computed, watch, watchEffect, isReactive, reactive, useAttrs, useId } from '#imports';
import { useIntersectionObserver } from '@vueuse/core';
import vClickOutside from '~dsc/directives/click-outside';

const props = defineProps({
    modelValue: { type: null, required: true },
    label: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    toggleIcon: { type: String, default: '' },
    error: { type: String, default: null },
    searchable: { type: [Boolean, Array], default: false },
    minimal: { type: Boolean },
    searchbox: { type: Boolean },
    iconBefore: { type: [Boolean, String, Object], default: false },
    disabled: { type: Boolean, default: false },
    eagerListIcons: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'input']);

const searchable = computed(() => props.searchbox || !!props.searchable);
const minimal = computed(() => props.searchbox || props.minimal);
const toggleIcon = computed(() => {
    if (props.toggleIcon) return props.toggleIcon;
    if (props.searchbox) return 'delete-text';

    return minimal.value ? 'chevron-down' : 'arrow-bothside';
});
const selectedIconBefore = computed(() => ({
    code: props.options.find(o => o.label === inputValue.value)?.iconBefore || props.options.find(o => o.label === inputValue.value)?.iconBefore?.code || '',
    size: props.options.find(o => o.label === inputValue.value)?.iconBefore?.size || 'l',
}));

const attrs = useAttrs();
const id = useId();

const computedId = computed(() => attrs.id || id);

const inputRef = ref(null);
const scrollableListRef = ref(null);
const listItemRefs = ref([]);

const filter = ref(false);
const inputValue = ref('');

watch(() => props.modelValue, setInputToModelValue, { immediate: true });

const open = ref(false);

const filteredOptions = computed(() => {
    if (!filter.value || !inputValue.value) return props.options;

    const query = new RegExp(inputValue.value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');

    return props.options.filter(o => Array.isArray(props.searchable)
        ? props.searchable.some(key => query.test(o[key]))
        : query.test(o.label));
});

const hideLabel = computed(() => minimal.value && inputValue.value);
const currentIndex = ref(0);
const currentItem = computed(() => filteredOptions.value[currentIndex.value]);

watchEffect(() => {
    if (open.value) filter.value = false;
    else setInputToModelValue();
});

function onClick(toggle = true) {
    currentIndex.value = props.options.findIndex(v => v.value === props.modelValue);
    inputRef.value.focus();
    if (toggle) open.value = searchable.value ? true : !open.value;
}

function onToggleClick() {
    if (props.disabled) return;
    if (props.searchbox) {
        inputValue.value = '';
        emit('update:modelValue', null);
    } else {
        open.value = !open.value;
    }

    onClick(false);
}

function onEnter() {
    if (!open.value || !currentItem.value) return;

    setActive(currentItem.value);
}

const scrollListTop = () => scrollableListRef.value.scrollTop = 0;
const scrollListBottom = () => scrollableListRef.value.scrollTop = scrollableListRef.value.scrollHeight;

function keyDownHome(e) {
    if (inputValue.value && searchable.value) return;

    e.preventDefault();
    currentIndex.value = 0;
    scrollListTop();
}

function keyDownEnd(e) {
    if (inputValue.value && searchable.value) return;

    e.preventDefault();
    currentIndex.value = filteredOptions.value.length - 1;
    scrollListBottom();
}

function keyUpOneItem() {
    if (currentIndex.value > 0) {
        currentIndex.value--;

        const list = scrollableListRef.value;
        const currentEl = listItemRefs.value[currentIndex.value];

        if (!list || !currentEl) return;

        const currentElBelowVisible = currentEl.offsetTop < list.scrollTop ;
        const currentElAboveVisible = currentEl.offsetTop > (list.scrollTop + list.clientHeight);

        if (currentElAboveVisible || currentElBelowVisible) list.scrollTop = currentEl.offsetTop;
    } else {
        scrollListTop();
    }
}

function keyDownOneItem() {
    open.value = true;

    if (currentIndex.value < filteredOptions.value.length - 1) {
        currentIndex.value++;

        const list = scrollableListRef.value;
        const currentEl = listItemRefs.value[currentIndex.value];

        if (!list || !currentEl) return;

        const currentElBottomEdgeOffsetTop = currentEl.offsetTop + currentEl.clientHeight;
        const currentElAboveVisible = currentEl.offsetTop < list.scrollTop;
        const currentElBelowVisible = currentElBottomEdgeOffsetTop > (list.clientHeight + list.scrollTop);

        if (currentElAboveVisible || currentElBelowVisible) list.scrollTop = currentElBottomEdgeOffsetTop - list.clientHeight;
    } else {
        scrollListBottom();
    }
}

function onInput() {
    open.value = true;
    filter.value = true;
    scrollableListRef.value.scrollTo(0, 0);

    if (props.searchbox) emit('input', inputValue.value);
}

function setActive(item) {
    emit('update:modelValue', item.value);
    inputRef.value.focus();
    open.value = false;
}

function setInputToModelValue() {
    const item = props.options.find(o => isReactive(props.modelValue) ? reactive(o.value) === props.modelValue : o.value === props.modelValue);

    inputValue.value = item?.label ?? '';
}

const visibleIcons = ref([]);
const containsListIcons = computed(() => props.options.some(item => item.iconBefore));

if (!props.eagerListIcons && containsListIcons.value) {
    watchEffect(() => {
        listItemRefs.value.forEach(li => {
            const { stop } = useIntersectionObserver(
                li,
                ([entry]) => {
                    if (entry?.isIntersecting && !visibleIcons.value.includes(entry.target)) {
                        visibleIcons.value.push(entry.target);
                        if (!props.searchable) stop();
                    }
                },
                { root: scrollableListRef },
            );
        });
    });
}
</script>

<style scoped>
.ds-select {
  position: relative;
}
.ds-select:hover .input::before {
  background: var(--color-base-black);
}
.ds-select.has-error:not(.minimal) .label {
  margin-top: 0;
  transform: scale(0.75);
  color: var(--color-error-400);
}
.ds-select.has-error .input {
  color: var(--color-error-400);
}
.ds-select.has-error .input-box::after {
  transform: scaleX(1);
  background-color: var(--color-error-400);
}
.ds-select .text-after {
  transition: all 0.2s ease-in-out;
  margin-top: 0.5rem;
  padding: 0 1rem;
  opacity: 0;
  font-size: 0.75rem;
  line-height: 1.42;
  height: 1rem;
  overflow: hidden;
}
.ds-select .text-after.is-active {
  opacity: 1;
}
.ds-select .text-after.error {
  color: var(--color-error-400);
}

.scrollable-list {
  max-height: 12.5rem;
  overflow-y: auto;
}

.list {
  margin: 0;
  list-style: none;
}

.ds-select.is-underlined .input::after {
  transform: scaleX(1);
}

.ds-select.is-open .icon {
  color: var(--color-base-grey-600);
}

.label {
  color: var(--color-base-grey-500);
  transition: transform 175ms ease-out;
  position: absolute;
  pointer-events: none;
  top: 0.5rem;
  transform-origin: 0 0;
  transform: translateY(1.125em);
}

.native-input {
  border: 0;
  background: none;
  width: 100%;
  padding: 1.6rem 2.5rem 0.313rem 0.75rem;
}
.native-input:focus {
  outline: none;
}
.native-input[type=button] {
  text-align: left;
  display: block;
}

.input {
  display: block;
  position: relative;
  background: var(--color-base-grey-100);
  line-height: 1.5;
  font-size: 1rem;
}
.input .icon {
  transition: all 0.2s ease-in-out;
  color: var(--color-base-grey-400);
  position: relative;
}
.input .toggle-btn,
.input .clear-btn {
  top: 0.75rem;
  right: 1rem;
  cursor: pointer;
}
.input .icon-before {
  top: 0;
  left: 0;
  margin: 0.5rem 0.75rem;
  position: absolute;
}
.input::before, .input::after {
  transition: all 0.2s ease-in-out;
  content: "";
  position: absolute;
  width: 100%;
  height: 1px;
  left: 0;
  bottom: 0;
}
.input::before {
  z-index: 0;
  background-color: var(--color-base-grey-300);
}
.input::after {
  z-index: 1;
  background: var(--color-primary-400);
  transform: scaleX(0);
}

.input + .scrollable-list {
  position: absolute;
  width: 100%;
  z-index: 3;
  box-shadow: inset 0 -2px 2px rgba(0, 0, 0, 0.1);
  background: var(--color-base-white-default);
}
.input + .scrollable-list .list-item {
  overflow: hidden;
  display: grid;
  gap: 0.5rem;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  color: var(--color-base-grey-600);
  cursor: pointer;
}
.input + .scrollable-list .list-item:hover {
  background: var(--color-base-grey-100);
}
.input + .scrollable-list .list-item.is-active {
  background: var(--color-base-grey-200);
}
.input + .scrollable-list .list-item.default {
  height: 2.5rem;
}

:not(.minimal) > .input + .scrollable-list .list-item {
  padding: 0 0.75rem;
}

.has-value .label {
  transform: translateY(0) scale(0.75);
}

.list-toggle-btn {
  display: inline-flex;
  position: absolute;
}

.ds-select:not(.minimal) .list-toggle-btn {
  top: 1.25em;
  right: 0.5rem;
}

.is-open .toggle-btn {
  transform: rotate(180deg);
}

.toggle-btn {
  transition: all 0.2s ease-in-out;
}

.minimal .input {
  border-radius: 3px;
}
.minimal .input::before, .minimal .input::after {
  display: none;
}
.minimal .label {
  border: 1px solid transparent;
  color: var(--color-base-grey-600);
  transform: translateY(0);
}
.minimal .label.is-active .selected-option-label {
  color: var(--color-base-black-default);
}
.minimal .native-input {
  padding: 0.5rem 3rem 0.5rem 0.75rem;
  border: 1px solid transparent;
}
.minimal:not(.searchbox).is-open .native-input,
.minimal .scrollable-list {
  border: 1px solid var(--color-base-grey-300);
  border-radius: 3px;
}
.minimal .scrollable-list {
  border-radius: 3px;
  box-shadow: 0 4px 8px 0 var(--color-base-black-mine-shaft-opaque);
  margin-top: 8px;
  top: auto;
  font-size: 1rem;
}
.minimal .scrollable-list .list-item {
  white-space: nowrap;
  padding-left: 1rem;
  padding-right: 1rem;
}

.searchbox .input::after {
  transition: all 0.2s ease-in-out;
  content: "";
  height: 2px;
  display: block;
  background-color: transparent;
}
.searchbox.is-open .input::after {
  background-color: var(--color-primary-400);
}
.searchbox .icon {
  color: var(--color-base-grey-800);
}

.has-icon-before .native-input,
.has-icon-before .label {
  padding-left: 3rem;
}
.has-icon-before.basic .icon-before {
  margin-top: 1.5rem;
}
.has-icon-before.basic .label {
  padding-left: 4rem;
}
.has-icon-before .icon, .has-icon-before.is-open .icon {
  color: var(--color-base-black-default);
}

.ds-select:not(.has-icon-before) .label {
  left: 0.75rem;
}
</style>
